import { Button, Divider, Flex, Input, Selector, SelectionOption } from '@/components'
import { TagsList, TagsListItem } from '@/components/core/TagsList/TagsList.component'
import { ColumnType, Table } from '@/components/derived'
import { Tokens } from '@/assets/tokens'
import { Layout } from '@/components/layout'
import { Routes } from '@/configs'
import { useRouting } from '@/hooks'
import { Api, Confirmation } from '@/modules'
import { DeleteFive, Edit, Search, Star } from '@icon-park/react'
import {
  CompanyResponse,
  CompanyUsersRequest,
  CompanyWithUsersResponse,
  Paged,
  StaffGroupBy,
  UserData,
  UserResponseRole,
  UserWithCompaniesResponse,
} from '@shared/models'
import { useEffect, useState } from 'react'
import { EditStaffCompanyModal } from './EditStaffCompanyModal.component'
import { EditStaffUserModal } from './EditStaffUserModal.component'
import { AdminAllCompaniesTag, Avatar, StaffTab, StaffTabsContainer } from './Staff.styles'

function formatRole(role: string): string {
  return role
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const roleOptions: SelectionOption[] = Object.keys(UserResponseRole).map((key) => {
  const value = UserResponseRole[key as keyof typeof UserResponseRole]
  return { value, text: formatRole(value) }
})

const pageSizeOptions: SelectionOption[] = [
  { value: 10, text: '10 por página' },
  { value: 25, text: '25 por página' },
  { value: 50, text: '50 por página' },
]

function formatDate(value: string): string {
  const date = new Date(value)
  const day = String(date.getUTCDate()).padStart(2, '0')
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const year = date.getUTCFullYear()
  return `${day}/${month}/${year}`
}

export function Staff() {
  const { navigate, currentPage, changeQueryParam } = useRouting()

  const { binder: companyBind, selectedOption: selectedCompany } = Selector.Binder()
  const { binder: roleBind, selectedOption: selectedRole } = Selector.Binder()
  const { binder: pageSizeBind, selectedOption: selectedPageSize } = Selector.Binder(pageSizeOptions[0])

  const [mode, setMode] = useState<StaffGroupBy>('user')
  const [searchText, setSearchText] = useState('')
  const [companySearchTerm, setCompanySearchTerm] = useState('')
  const [editingUserID, setEditingUserID] = useState<string | null>(null)
  const [editingCompanyID, setEditingCompanyID] = useState<string | null>(null)

  const [loadByUser, isLoadingByUser, staffByUser, errorByUser] =
    Api.LazyQuery<Paged<UserWithCompaniesResponse>>('/staff/')
  const [loadByCompany, isLoadingByCompany, staffByCompany, errorByCompany] =
    Api.LazyQuery<Paged<CompanyWithUsersResponse>>('/staff/')

  const [, , companies, errorCompanies] = Api.Query<Paged<CompanyResponse>>('companies?size=10000&orderBy=name:asc')
  const [, , allUsers] = Api.Query<UserData[]>('common/users')
  const { run: deleteStaff } = Api.Command<void>(Api.Methods.DELETE, 'staff/:userID/:companyID')
  const { run: createStaff } = Api.Command<void>(Api.Methods.POST, 'staff/')

  if (errorByUser || errorByCompany || errorCompanies) {
    throw Error('Unexpected error. Contact the system administrator.')
  }

  const { open: openConfirmation } = Confirmation.Hook({
    icon: DeleteFive,
    title: 'Você tem certeza?',
    text: 'Esta ação não tem como ser desfeita. Esse vínculo de assessor-empresa será perdido.',
    confirmText: 'Sim, apagar vínculo',
  })

  const isUserMode = mode === 'user'
  const total = isUserMode ? staffByUser?.total : staffByCompany?.total
  const pages = isUserMode ? staffByUser?.pages : staffByCompany?.pages
  const editingUser = staffByUser?.elements.find((user) => user.userID === editingUserID) ?? null
  const editingCompany = staffByCompany?.elements.find((company) => company.companyID === editingCompanyID) ?? null

  const filteredCompanies =
    companies?.elements.filter((company) => company.name.toLowerCase().includes(companySearchTerm.toLowerCase())) ?? []

  const pageSize = Number(selectedPageSize?.value ?? 10)

  function reload(): void {
    if (mode === 'company') {
      loadByCompany({ groupBy: 'company', search: searchText || undefined, page: currentPage, size: pageSize })
    } else {
      loadByUser({
        groupBy: 'user',
        search: searchText || undefined,
        company: selectedCompany?.value,
        role: selectedRole?.value,
        page: currentPage,
        size: pageSize,
      })
    }
  }

  useEffect(() => {
    reload()
  }, [mode, currentPage, searchText, selectedCompany, selectedRole, pageSize])

  useEffect(() => {
    setCompanySearchTerm('')
  }, [selectedCompany])

  function handleModeChange(nextMode: StaffGroupBy): void {
    if (nextMode === mode) return
    setMode(nextMode)
    changeQueryParam({ param: 'pagina', value: '1' })
  }

  function handleDeleteStaff(userID: string, companyID: string): void {
    openConfirmation(() =>
      deleteStaff<void>({
        binds: [
          ['userID', userID],
          ['companyID', companyID],
        ],
        onSuccess: () => reload(),
      }),
    )
  }

  function handleAddCompany(userID: string, companyID: string): void {
    createStaff<CompanyUsersRequest>({
      input: { userID, companyIDs: [companyID] },
      onSuccess: () => reload(),
    })
  }

  const userColumns: ColumnType<UserWithCompaniesResponse>[] = [
    {
      key: 'userProfilePic',
      width: 56,
      render: (_, item) => (item.userProfilePic ? <Avatar src={item.userProfilePic} alt={item.userName} /> : null),
    },
    { key: 'userName', title: 'Usuário' },
    { key: 'userEmail', title: 'E-mail' },
    {
      key: 'role',
      title: 'Perfil',
      render: (_, item) =>
        item.role === UserResponseRole.AdminMaster ? 'Admin Master'
        : item.isAdmin ? 'Administrador'
        : formatRole(item.role),
    },
    {
      key: 'regions',
      title: 'Região',
      render: (_, item) => (item.regions?.length ? item.regions.join(', ') : '—'),
    },
    {
      key: 'companies',
      title: 'Empresas',
      render: (_, item) =>
        item.isAdmin ?
          <AdminAllCompaniesTag>Todas as empresas</AdminAllCompaniesTag>
        : <TagsList
            emptyText="Nenhuma empresa vinculada"
            tags={item.companies.map(
              (company): TagsListItem => ({
                id: company.companyID,
                text: company.companyName,
                tooltip: `Vinculado em ${formatDate(company.linkedDate)}`,
              }),
            )}
          />,
    },
    {
      key: 'actions',
      title: 'Ações',
      align: 'center',
      render: (_, item) => (
        <Button.Root $variant="link" onClick={() => setEditingUserID(item.userID)}>
          <Button.Icon value={Edit} color={Tokens.ColorTextSecondary} />
        </Button.Root>
      ),
    },
  ]

  const companyColumns: ColumnType<CompanyWithUsersResponse>[] = [
    { key: 'companyName', title: 'Empresa' },
    { key: 'userCount', title: 'Usuários vinculados', align: 'center' },
    {
      key: 'users',
      title: 'Usuários',
      render: (_, item) => (
        <TagsList
          emptyText="Nenhum usuário vinculado"
          tags={item.users.map(
            (user): TagsListItem => ({
              id: user.userID,
              text: user.userName,
              tooltip:
                user.isAdmin ? 'Acesso por ser Administrador'
                : user.linkedDate ? `Vinculado em ${formatDate(user.linkedDate)}`
                : undefined,
            }),
          )}
        />
      ),
    },
    {
      key: 'actions',
      title: 'Ações',
      align: 'center',
      render: (_, item) => (
        <Button.Root $variant="link" onClick={() => setEditingCompanyID(item.companyID)}>
          <Button.Icon value={Edit} color={Tokens.ColorTextSecondary} />
        </Button.Root>
      ),
    },
  ]

  return (
    <Layout.Root>
      <Layout.Padder>
        <Flex $type="column" $justify="flex-start" $align="stretch" $gap={16}>
          <StaffTabsContainer>
            <StaffTab $active={isUserMode} onClick={() => handleModeChange('user')}>
              Por usuário
            </StaffTab>
            <StaffTab $active={!isUserMode} onClick={() => handleModeChange('company')}>
              Por empresa
            </StaffTab>
          </StaffTabsContainer>

          <Flex $type="row" $justify="flex-start" $align="flex-start" $gap={16} $fit>
            <Input
              icon={Search}
              placeholder={isUserMode ? 'Pesquisar por usuário...' : 'Pesquisar por empresa...'}
              onChange={(value: string) => setSearchText(value)}
            />
            {isUserMode && (
              <Selector.Searchable
                {...companyBind}
                placeholder="Empresa"
                options={filteredCompanies.map((item) => ({ text: item.name, value: item.id }))}
                onSearch={(searchParam: string): void => {
                  setCompanySearchTerm(searchParam)
                }}>
                <Selector.Icon value={Star} color={Tokens.ColorUIPrimary} />
              </Selector.Searchable>
            )}
            {isUserMode && <Selector.Default {...roleBind} placeholder="Perfil" options={roleOptions} />}
            <Selector.Default {...pageSizeBind} options={pageSizeOptions} />
          </Flex>

          <Divider.Root>
            <Divider.Title value="Usuários" />
            <Divider.Subtitle value={isUserMode ? `${total ?? 0} usuários` : `${total ?? 0} empresas`} />
          </Divider.Root>

          {isUserMode ?
            <Table isLoading={isLoadingByUser} data={staffByUser?.elements} columns={userColumns} totalPages={pages} />
          : <Table
              isLoading={isLoadingByCompany}
              data={staffByCompany?.elements}
              columns={companyColumns}
              totalPages={pages}
            />
          }
        </Flex>
        <Flex $type="row" $justify="flex-end" $align="center" $fit>
          <Button.Root $variant="secondary" onClick={() => navigate(Routes.Home)} $size={100}>
            <Button.Text value="Voltar" />
          </Button.Root>
        </Flex>
      </Layout.Padder>

      <EditStaffUserModal
        user={editingUser}
        companyOptions={companies?.elements.map((company) => ({ value: company.id, text: company.name })) ?? []}
        onClose={() => setEditingUserID(null)}
        onSaved={reload}
        onRemoveCompany={handleDeleteStaff}
        onAddCompany={handleAddCompany}
      />
      <EditStaffCompanyModal
        company={editingCompany}
        userOptions={allUsers?.map((user) => ({ value: user.id, text: user.name })) ?? []}
        onClose={() => setEditingCompanyID(null)}
        onRemoveUser={handleDeleteStaff}
        onAddUser={handleAddCompany}
      />
    </Layout.Root>
  )
}
